/* eslint-disable react-hooks/exhaustive-deps */
import { Checkbox, Radio, Select } from "antd";
import {
  Dispatch,
  forwardRef,
  ReactNode,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import CaretDownIcon from "../../../../../assets/icons/CaretDownIcon";
import CaretUpIcon from "../../../../../assets/icons/CaretUpIcon";
import CloseIcon from "../../../../../assets/icons/CloseIcon";

import TextArea from "antd/es/input/TextArea";
import { useUploadPhotoMutation } from "../../../../../features/superAdmin/ticketService/ticketServiceApi";
import useError from "../../../../../hooks/useError";
import { toast } from "../../../../../lib/toast";

type ImageMap = {
  [key: string]: File[];
};

const AccordionContent = ({ children }: { children: ReactNode }) => {
  return <div className="p-4">{children}</div>;
};

const ContentFromTemplate = forwardRef(function AccordionSection(
  {
    othersFieldsData = [],
    fileUploadFieldsData = [],
    setUploading,
  }: {
    othersFieldsData?: any[];
    fileUploadFieldsData?: any[];
    setUploading: Dispatch<SetStateAction<boolean>>;
  },
  ref,
) {
  const [images, setImages] = useState<ImageMap>({});
  const [openItemId, setOpenItemId] = useState<number | null>(null);
  const [uploadedImageUrls, setUploadedImageUrls] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<
    {
      required_field_id: number;
      question_text: string;
      options: {
        id: number;
        required_field_option: string;
      }[];
    }[]
  >([]);
  const [answers, setAnswers] = useState<
    {
      required_field_id: number;
      question_text: string;
      value: string;
    }[]
  >([]);
  const [
    uploadPhoto,
    {
      error: uploadPhotoError,
      isError: uploadPhotoISError,
      isLoading: uploading,
    },
  ] = useUploadPhotoMutation();
  useError(uploadPhotoISError, uploadPhotoError, "Upload photo fail");

  useEffect(() => {
    if (uploading) {
      setUploading(true);
    } else {
      setUploading(false);
    }
  }, [uploading]);

  // Split the array into two halves
  const middleIndex = Math.ceil(othersFieldsData.length / 2);
  const firstHalf = othersFieldsData.slice(0, middleIndex);
  const secondHalf = othersFieldsData.slice(middleIndex);

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    option: string,
    optionId: number,
    item: any,
  ) => {
    const isChecked = e.target.checked;

    setSelectedOptions((prev) => {
      const existingField = prev.find(
        (field) => field.required_field_id === item.id,
      );

      if (isChecked) {
        if (existingField) {
          const updatedOptions = [
            ...existingField.options,
            { id: optionId, required_field_option: option },
          ];

          return prev.map((field) =>
            field.required_field_id === item.id
              ? { ...field, options: updatedOptions }
              : field,
          );
        } else {
          return [
            ...prev,
            {
              required_field_id: item.id,
              question_text: item.question_text,
              options: [{ id: optionId, required_field_option: option }],
            },
          ];
        }
      } else {
        if (!existingField) return prev;

        const filteredOptions = existingField.options.filter(
          (opt) => opt.required_field_option !== option,
        );

        if (filteredOptions.length === 0) {
          return prev.filter((field) => field.required_field_id !== item.id);
        }

        return prev.map((field) =>
          field.required_field_id === item.id
            ? { ...field, options: filteredOptions }
            : field,
        );
      }
    });
  };

  const handleRadioChange = (option: string, optionId: number, item: any) => {
    setSelectedOptions((prev) => {
      const existingField = prev.find(
        (field) => field.required_field_id === item.id,
      );

      const newSelection = {
        required_field_id: item.id,
        question_text: item.question_text,
        options: [{ id: optionId, required_field_option: option }],
      };

      if (existingField) {
        return prev.map((field) =>
          field.required_field_id === item.id ? newSelection : field,
        );
      } else {
        return [...prev, newSelection];
      }
    });
  };

  const handleShortAnswerChange = (value: string, item: any) => {
    setAnswers((prev) => {
      const existingField = prev.find(
        (field) => field.required_field_id === item.id,
      );

      const newSelection = {
        required_field_id: item.id,
        question_text: item.question_text,
        value,
      };

      if (existingField) {
        return prev.map((field) =>
          field.required_field_id === item.id ? newSelection : field,
        );
      } else {
        return [...prev, newSelection];
      }
    });
  };

  useImperativeHandle(ref, () => ({
    handleRaiseTicket,
  }));

  const handleRaiseTicket = () => {
    const uploadedImageData = fileUploadFieldsData.map((item: any) => ({
      question_text: item?.question_text,
      required_field_id: item.id,
      photo_urls: uploadedImageUrls,
    }));

    const required_fields = [
      ...selectedOptions,
      ...uploadedImageData,
      ...answers,
    ];

    return {
      required_fields,
    };
  };

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    item: any,
  ) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    // Constraints
    const maxFileSize = (item?.settings?.max_file_size || 32) * 1024 * 1024; // in bytes
    const maxFiles = item?.settings?.max_number_of_files || 3;

    const existingFiles = images[item?.id]?.length || 0;

    // Check if new files exceed max allowed count
    if (existingFiles + files.length > maxFiles) {
      toast("error", `You can only upload a maximum of ${maxFiles} files.`);
      return;
    }

    // Filter out files that exceed max file size
    const validFiles = files.filter((file) => {
      if (file.size > maxFileSize) {
        toast(
          "error",
          `"${file.name}" exceeds the max file size of ${
            item?.settings?.max_file_size || 32
          }MB.`,
        );
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Update preview images
    setImages((prev) => ({
      ...prev,
      [item?.id]: [...(prev[item?.id] || []), ...validFiles],
    }));

    // Upload valid files
    validFiles.forEach((file) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);

      uploadPhoto(formData)
        .unwrap()
        .then((res: any) => {
          if (res?.Location) {
            setUploadedImageUrls((prev) => [...prev, res.Location]);
          }
        })
        .catch((err) => {
          console.error("Image upload failed", err);
        });
    });

    e.target.value = "";
  };

  const handleImageDrop = (
    e: React.DragEvent<HTMLLabelElement>,
    item: any,
  ) => {
   e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Constraints
    const maxFileSize = (item?.settings?.max_file_size || 32) * 1024 * 1024; // in bytes
    const maxFiles = item?.settings?.max_number_of_files || 3;

    const existingFiles = images[item?.id]?.length || 0;

    // Check if new files exceed max allowed count
    if (existingFiles + files.length > maxFiles) {
      toast("error", `You can only upload a maximum of ${maxFiles} files.`);
      return;
    }

    // Filter out files that exceed max file size
    const validFiles = files.filter((file) => {
      if (file.size > maxFileSize) {
        toast(
          "error",
          `"${file.name}" exceeds the max file size of ${
            item?.settings?.max_file_size || 32
          }MB.`,
        );
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Update preview images
    setImages((prev) => ({
      ...prev,
      [item?.id]: [...(prev[item?.id] || []), ...validFiles],
    }));

    // Upload valid files
    validFiles.forEach((file) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);

      uploadPhoto(formData)
        .unwrap()
        .then((res: any) => {
          if (res?.Location) {
            setUploadedImageUrls((prev) => [...prev, res.Location]);
          }
        })
        .catch((err) => {
          console.error("Image upload failed", err);
        });
    });

  };

  const removeImage = (fieldId: number, index: number) => {
    setImages((prev) => {
      const updated = [...(prev[fieldId] || [])].filter((_, i) => i !== index);
      return { ...prev, [fieldId]: updated };
    });
    setUploadedImageUrls((prev) => {
      const updated = [...prev].filter((_, i) => i !== index);
      return [...updated];
    });
  };

  return (
    <>
      <div className="my-6">
        {/* <div className="grid grid-cols-2 gap-4"> */}
        <div className="">
          <div>
            {firstHalf.map((item) => {
              const isOpen = openItemId === item.id;
              return (
                <div key={item.id}>
                  <div
                    className="flex justify-between shadow rounded-md p-4 items-center cursor-pointer mb-4"
                    onClick={() => {
                      setOpenItemId((prev) =>
                        prev === item.id ? null : item.id,
                      );
                    }}
                  >
                    <div>{item?.question_text}</div>
                    <div>{isOpen ? <CaretUpIcon /> : <CaretDownIcon />}</div>
                  </div>

                  {isOpen && item?.required_field_type === "checkboxes" && (
                    <AccordionContent>
                      <div className="text-[#3D4A5C]">
                        {item?.options?.map((brand: any, index: number) => (
                          <div key={index}>
                            <Checkbox
                              checked={selectedOptions
                                .find((f) => f.required_field_id === item.id)
                                ?.options.some(
                                  (opt) =>
                                    opt.required_field_option ===
                                    brand?.required_field_option,
                                )}
                              onChange={(e: any) =>
                                handleCheckboxChange(
                                  e,
                                  brand?.required_field_option,
                                  brand?.id,
                                  item,
                                )
                              }
                            >
                              {brand?.required_field_option}
                            </Checkbox>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  )}
                  {isOpen && item?.required_field_type === "short_answer" && (
                    <AccordionContent>
                      <div className="text-[#3D4A5C]">
                        {/* <span></span> */}
                        <TextArea
                          onChange={(e) => {
                            handleShortAnswerChange(e.target.value, item);
                          }}
                          defaultValue={
                            answers.find((f) => f.required_field_id === item.id)
                              ?.value
                          }
                          required={item?.is_required}
                          placeholder="Write description"
                        />
                      </div>
                    </AccordionContent>
                  )}

                  {isOpen &&
                    item?.required_field_type === "multiple_choice" && (
                      <AccordionContent>
                        <div className="text-[#3D4A5C]">
                          <Radio.Group
                            onChange={(e) =>
                              handleRadioChange(
                                e.target.value,
                                item.options.find(
                                  (opt: any) =>
                                    opt.required_field_option ===
                                    e.target.value,
                                )?.id,
                                item,
                              )
                            }
                            value={
                              selectedOptions.find(
                                (f) => f.required_field_id === item.id,
                              )?.options[0]?.required_field_option
                            }
                          >
                            {item?.options?.map((brand: any, index: number) => (
                              <Radio
                                key={index}
                                value={brand?.required_field_option}
                                style={{
                                  display: "block",
                                }}
                              >
                                {brand?.required_field_option}
                              </Radio>
                            ))}
                          </Radio.Group>
                        </div>
                      </AccordionContent>
                    )}

                  {isOpen && item?.required_field_type === "dropdown" && (
                    <AccordionContent>
                      <div className="text-[#3D4A5C]">
                        <Select
                          size="middle"
                          className="w-full"
                          placeholder="Please select"
                          value={
                            selectedOptions.find(
                              (f) => f.required_field_id === item.id,
                            )?.options[0]?.required_field_option
                          }
                          onChange={(value) => {
                            const selectedId = item.options.find(
                              (opt: any) => opt.required_field_option === value,
                            )?.id;

                            handleRadioChange(value, selectedId, item);
                          }}
                          options={item?.options?.map((brand: any) => ({
                            label: brand.required_field_option,
                            value: brand.required_field_option,
                          }))}
                        />
                      </div>
                    </AccordionContent>
                  )}
                </div>
              );
            })}
          </div>

          <div>
            {secondHalf.map((item) => {
              const isOpen = openItemId === item.id;

              return (
                <div key={item.id}>
                  <div
                    className="flex justify-between shadow rounded-md p-4 items-center cursor-pointer mb-4"
                    onClick={() =>
                      setOpenItemId((prev) =>
                        prev === item.id ? null : item.id,
                      )
                    }
                  >
                    <div>{item?.question_text}</div>
                    <div>{isOpen ? <CaretUpIcon /> : <CaretDownIcon />}</div>
                  </div>

                  {isOpen && item?.required_field_type === "checkboxes" && (
                    <AccordionContent>
                      <div className="text-[#3D4A5C]">
                        {item?.options?.map((brand: any, index: number) => (
                          <div key={index}>
                            <Checkbox
                              checked={selectedOptions
                                .find((f) => f.required_field_id === item.id)
                                ?.options.some(
                                  (opt) =>
                                    opt.required_field_option ===
                                    brand?.required_field_option,
                                )}
                              onChange={(e: any) =>
                                handleCheckboxChange(
                                  e,
                                  brand?.required_field_option,
                                  brand?.id,
                                  item,
                                )
                              }
                            >
                              {brand?.required_field_option}
                            </Checkbox>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  )}
                  {isOpen && item?.required_field_type === "short_answer" && (
                    <AccordionContent>
                      <div className="text-[#3D4A5C]">
                        {/* <span></span> */}
                        <TextArea
                          onChange={(e) =>
                            handleShortAnswerChange(e.target.value, item)
                          }
                          defaultValue={
                            answers.find((f) => f.required_field_id === item.id)
                              ?.value
                          }
                          // required={answers?.short_answer}
                          placeholder="Write description"
                        />
                      </div>
                    </AccordionContent>
                  )}
                  {isOpen &&
                    item?.required_field_type === "multiple_choice" && (
                      <AccordionContent>
                        <div className="text-[#3D4A5C]">
                          <Radio.Group
                            onChange={(e) =>
                              handleRadioChange(
                                e.target.value,
                                item.options.find(
                                  (opt: any) =>
                                    opt.required_field_option ===
                                    e.target.value,
                                )?.id,
                                item,
                              )
                            }
                            value={
                              selectedOptions.find(
                                (f) => f.required_field_id === item.id,
                              )?.options[0]?.required_field_option
                            }
                          >
                            {item?.options?.map((brand: any, index: number) => (
                              <Radio
                                key={index}
                                value={brand?.required_field_option}
                                style={{
                                  display: "block",
                                }}
                              >
                                {brand?.required_field_option}
                              </Radio>
                            ))}
                          </Radio.Group>
                        </div>
                      </AccordionContent>
                    )}
                  {isOpen && item?.required_field_type === "dropdown" && (
                    <AccordionContent>
                      <div className="text-[#3D4A5C]">
                        <Select
                          size="middle"
                          className="w-full"
                          placeholder="Please select"
                          value={
                            selectedOptions.find(
                              (f) => f.required_field_id === item.id,
                            )?.options[0]?.required_field_option
                          }
                          onChange={(value) => {
                            const selectedId = item.options.find(
                              (opt: any) => opt.required_field_option === value,
                            )?.id;

                            handleRadioChange(value, selectedId, item);
                          }}
                          options={item?.options?.map((brand: any) => ({
                            label: brand.required_field_option,
                            value: brand.required_field_option,
                          }))}
                        />
                      </div>
                    </AccordionContent>
                  )}
                  {/* {isOpen && item?.required_field_type === "dropdown" && (
                    <AccordionContent>
                      <div className="text-[#3D4A5C]">
                        <Radio.Group
                          onChange={(e) =>
                            handleRadioChange(
                              e.target.value,
                              item.options.find(
                                (opt: any) =>
                                  opt.required_field_option === e.target.value,
                              )?.id,
                              item,
                            )
                          }
                          value={
                            selectedOptions.find(
                              (f) => f.required_field_id === item.id,
                            )?.options[0]?.required_field_option
                          }
                        >
                          {item?.options?.map((brand: any, index: number) => (
                            <Radio
                              key={index}
                              value={brand?.required_field_option}
                              style={{
                                display: "block",
                              }}
                            >
                              {brand?.required_field_option}
                            </Radio>
                          ))}
                        </Radio.Group>
                      </div>
                    </AccordionContent>
                  )} */}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div>
        {fileUploadFieldsData?.map((item, idx) => {
          return (
            <div key={idx}>
              <div className="flex justify-between items-center mt-4">
                {/* <div>{item?.question_text} Attachment</div> */}
                <div>{item?.question_text}</div>
              </div>

              <div className="flex items-center flex-wrap gap-4 mt-2">
                {/* Show uploaded image previews */}
                {images[item?.id]?.map((file: any, index: any) => (
                  <div className="relative w-44 h-32" key={index}>
                    <img
                      src={URL.createObjectURL(file)}
                      alt="upload"
                      className="w-full h-full object-cover rounded-md border border-[#D7DFE9]"
                    />
                    <button
                      disabled={uploading}
                      className="absolute top-1 right-1 cursor-pointer pt-1 pr-1 disabled:opacity-80 disabled:cursor-not-allowed"
                      onClick={() => removeImage(item?.id, index)}
                    >
                      <CloseIcon />
                    </button>
                  </div>
                ))}

                {/* Upload UI */}
                {(images[item.id]?.length || 0) <
                  item?.settings?.max_number_of_files && (
                  <label
                    className={`${
                      (images[item.id]?.length || 0) === 0 ? "w-full" : "w-44"
                    } h-32 p-3 bg-[#FFFFFF] rounded-md border border-[#D7DFE9] inline-flex justify-center items-center gap-2.5 overflow-hidden cursor-pointer ${
                      uploading && "opacity-80 cursor-not-allowed"
                    }`}
                     onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleImageDrop(e, item)}
                  >
                    <div className="text-center">
                      <span className="text-[#8897AE] text-sm font-normal leading-normal">
                        Drag and Drop
                        <br />
                        Or
                        <br />
                      </span>
                      <span className="text-[#3D4A5C] text-sm font-medium underline leading-normal">
                        Upload File
                      </span>
                      <input
                        disabled={uploading}
                        hidden
                        type="file"
                        accept="image/*"
                        className="!hidden"
                        multiple
                        onChange={(e) => handleImageChange(e, item)}
                      />
                    </div>
                  </label>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
});

export default ContentFromTemplate;
